/**
 * RecordSlideoutChrome — the body-portaled `SlideoutMenu` with every record
 * region wired into its slots: header content, header actions, body, footer.
 *
 * The regions read the surface state from `RecordSurfaceContext`, so this
 * component is the same whether that context comes from the real
 * `RecordSurfaceProvider` (`SingleSlideout`) or a fixed
 * `RecordSurfaceStateProvider` (stories, tests). It owns no data and no
 * dismissal logic — the host passes `isOpen` / `onOpenChange` / `onBack`
 * already routed through whatever guard it needs.
 */

import type { ReactElement } from 'react';

import { SlideoutMenu } from '@/lib/base/overlays/slideout-menu';

import { RECORD_HEADER_ALIGN, RECORD_HEADER_CLASS } from './record-layout';
import {
  RecordSlideoutBody,
  RecordSlideoutFooter,
  RecordSlideoutHeaderActions,
  RecordSlideoutHeaderContent,
  RecordSlideoutHeaderIdentity,
} from './record-slideout-regions';

// Slot contents are static elements — the regions read everything from
// context — so they are created once rather than on every render.
const HEADER_SLOT = <RecordSlideoutHeaderContent />;
const HEADER_ACTIONS_SLOT = <RecordSlideoutHeaderActions />;
const HEADER_SECOND_ROW_SLOT = <RecordSlideoutHeaderIdentity />;
const FOOTER_SLOT = <RecordSlideoutFooter />;

// The body brings its own gutter and the footers their own padding, so the
// chrome's defaults are zeroed — including the `md:` variants, which
// tailwind-merge treats as a separate group from the bare utility.
const CONTENT_CLASS = 'px-0 md:px-0';
const FOOTER_CLASS = 'p-0 md:px-0 shadow-none';

export interface RecordSlideoutChromeProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  /** Published on the surface context as `back`; omit when there is nowhere to go. */
  onBack?: () => void;
  dialogAriaLabel?: string;
  initialWidth?: number;
  minWidth?: number;
  maxWidth?: number;
  onWidthChange?: (width: number) => void;
  /** MUST be a stable reference (it is an effect dependency in SlideoutMenu). */
  onResizingChange?: (isResizing: boolean) => void;
}

export function RecordSlideoutChrome({
  isOpen,
  onOpenChange,
  onBack,
  dialogAriaLabel,
  initialWidth,
  minWidth,
  maxWidth,
  onWidthChange,
  onResizingChange,
}: RecordSlideoutChromeProps): ReactElement {
  return (
    <SlideoutMenu
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      resizable
      initialWidth={initialWidth}
      minWidth={minWidth}
      maxWidth={maxWidth}
      onWidthChange={onWidthChange}
      // Publishes drag start/end so surfaces sized against this panel (the
      // builder-mode chat column) can suppress their width transition and
      // track the pointer 1:1 instead of lagging it.
      onResizingChange={onResizingChange}
      showBackdrop={false}
      nonModal
      instance='crm-object'
      dialogAriaLabel={dialogAriaLabel}
      header={HEADER_SLOT}
      headerActions={HEADER_ACTIONS_SLOT}
      headerSecondRow={HEADER_SECOND_ROW_SLOT}
      headerAlign={RECORD_HEADER_ALIGN}
      headerClassName={RECORD_HEADER_CLASS}
      contentClassName={CONTENT_CLASS}
      footer={FOOTER_SLOT}
      footerClassName={FOOTER_CLASS}
      onBack={onBack}
    >
      <RecordSlideoutBody />
    </SlideoutMenu>
  );
}
